/**
 * Validacions de camps reutilitzables (frontend).
 * Camps buits: vàlids, tret que la pantalla els marqui com a obligatoris.
 * DNI / NIE / NIF: si el format es reconeix, es comprova la lletra o el control
 * i es retorna un avís (no un error), per no bloquejar documents estrangers o casos dubtosos.
 */

function text(valor) {
  return valor == null ? "" : String(valor).trim();
}

export function esBuit(valor) {
  return text(valor) === "";
}

export function validarEmail(valor) {
  if (esBuit(valor)) return { ok: true };
  const s = text(valor);
  const ok = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(s);
  if (!ok) {
    return { ok: false, missatge: "Cal un format del tipus nom@domini.cat." };
  }
  return { ok: true };
}

export function validarTelefon(valor) {
  if (esBuit(valor)) return { ok: true };
  const s = text(valor);
  if (!/^[+0-9][0-9\s./()+\-#]*$/.test(s)) {
    return {
      ok: false,
      missatge: "Només dígits i símbols habituals (+, espai, -, ., /, parèntesis).",
    };
  }
  const digits = s.replace(/\D/g, "");
  if (digits.length < 6 || digits.length > 15) {
    return { ok: false, missatge: "Ha de tenir entre 6 i 15 dígits." };
  }
  return { ok: true };
}

export function validarUrl(valor) {
  if (esBuit(valor)) return { ok: true };
  const s = text(valor);
  const candidate = /^https?:\/\//i.test(s) ? s : `https://${s}`;
  try {
    const u = new URL(candidate);
    if (!u.hostname || !u.hostname.includes(".")) {
      return { ok: false, missatge: "No sembla una adreça web vàlida." };
    }
    return { ok: true };
  } catch {
    return { ok: false, missatge: "No sembla una adreça web vàlida." };
  }
}

export function validarCodiPostal(valor) {
  if (esBuit(valor)) return { ok: true };
  const s = text(valor);
  if (!/^[A-Za-z0-9\s-]{2,10}$/.test(s)) {
    return { ok: false, missatge: "Només lletres, números, espais o guions." };
  }
  return { ok: true };
}

const LLETRES_DNI = "TRWAGMYFPDXBNJZSQVHLCKE";
const LLETRES_CIF = "JABCDEFGHI";
const LLETRES_CIF_INICI = "ABCDEFGHJNPQRSUVW";
const CIF_CONTROL_LLETRA = "PQSNW";
const CIF_CONTROL_DIGIT = "ABEH";

export function normalitzarDocument(valor) {
  return text(valor).toUpperCase().replace(/[\s.\-/]/g, "");
}

export function lletraDni(numeros) {
  const n = parseInt(String(numeros).replace(/\D/g, ""), 10);
  if (!Number.isFinite(n)) return null;
  return LLETRES_DNI[n % 23];
}

function controlCif(cif) {
  const digits = cif.slice(1, 8);
  let suma = 0;
  for (let i = 0; i < 7; i += 1) {
    let n = Number(digits[i]);
    if (i % 2 === 0) {
      n *= 2;
      if (n > 9) n = Math.floor(n / 10) + (n % 10);
    }
    suma += n;
  }
  const unitat = suma % 10;
  const digit = unitat === 0 ? 0 : 10 - unitat;
  return { digit: String(digit), lletra: LLETRES_CIF[digit] };
}

function cifControlCorrecte(cif) {
  const inici = cif[0];
  const control = cif[8];
  const { digit, lletra } = controlCif(cif);
  if (CIF_CONTROL_LLETRA.includes(inici)) return control === lletra;
  if (CIF_CONTROL_DIGIT.includes(inici)) return control === digit;
  return control === digit || control === lletra;
}

/**
 * @returns {{ ok: boolean, tipus: string|null, avis?: string, missatge?: string }}
 */
export function validarDocumentIdentitat(valor) {
  if (esBuit(valor)) return { ok: true, tipus: null };
  const s = normalitzarDocument(valor);

  if (/^\d{8}[A-Z]$/.test(s)) {
    const esperada = lletraDni(s.slice(0, 8));
    if (esperada && s[8] !== esperada) {
      return {
        ok: true,
        tipus: "DNI",
        avis: `La lletra del DNI no coincideix. Hauria de ser ${esperada}.`,
      };
    }
    return { ok: true, tipus: "DNI" };
  }

  if (/^[XYZ]\d{7}[A-Z]$/.test(s)) {
    const prefix = { X: "0", Y: "1", Z: "2" }[s[0]];
    const esperada = lletraDni(prefix + s.slice(1, 8));
    if (esperada && s[8] !== esperada) {
      return {
        ok: true,
        tipus: "NIE",
        avis: `La lletra del NIE no coincideix. Hauria de ser ${esperada}.`,
      };
    }
    return { ok: true, tipus: "NIE" };
  }

  if (/^[KLM]\d{7}[A-Z]$/.test(s)) {
    const esperada = lletraDni(s.slice(1, 8));
    if (esperada && s[8] !== esperada) {
      return {
        ok: true,
        tipus: "NIF",
        avis: `La lletra del NIF no coincideix. Hauria de ser ${esperada}.`,
      };
    }
    return { ok: true, tipus: "NIF" };
  }

  if (new RegExp(`^[${LLETRES_CIF_INICI}]\\d{7}[0-9A-J]$`).test(s)) {
    if (!cifControlCorrecte(s)) {
      return {
        ok: true,
        tipus: "NIF",
        avis: "El dígit o la lletra de control del NIF (A/B/…) no coincideix.",
      };
    }
    return { ok: true, tipus: "NIF" };
  }

  return { ok: true, tipus: null };
}

export function aplicarValidacio(errors, avisos, etiqueta, resultat) {
  if (!resultat) return;
  if (resultat.ok === false && resultat.missatge) {
    errors.push(`${etiqueta}: ${resultat.missatge}`);
  }
  if (resultat.avis) {
    avisos.push(`${etiqueta}: ${resultat.avis}`);
  }
}
